import { leaguesV2Api } from "../leaguesV2Api";
import type { Clubhouse_LeagueAdjudicationQueueDto } from "../../types/adjudicationQueue";
import type {
  Clubhouse_LgAdjudicationIssueSeverityV2,
  Clubhouse_LgAdjudicationIssueTypeV2,
} from "../../enums";

// GET /api/leagues/adjudication-queue — operator-scoped (not facility-scoped),
// same scope as the dashboard. All filtering and paging happen server-side; the
// multi-value filters are sent as comma-separated params (e.g.
// severity=Warning,Blocking).
export interface GetAdjudicationQueueArgs {
  /** 1-based page index. */
  page?: number;
  /** Page size (max 100). */
  pageSize?: number;
  /** League filter — one or more operator-owned league ids (CSV param). */
  leagueIds?: number[];
  severities?: Clubhouse_LgAdjudicationIssueSeverityV2[];
  issueTypes?: Clubhouse_LgAdjudicationIssueTypeV2[];
  /** Case-insensitive search on league or participant name. */
  search?: string;
}

// Only send params the caller actually set; empty arrays and empty search are
// dropped so they don't widen the query string needlessly.
function pickQueueParams(
  args: GetAdjudicationQueueArgs,
): Record<string, string | number> | undefined {
  const params: Record<string, string | number> = {};
  if (args.page !== undefined) params.page = args.page;
  if (args.pageSize !== undefined) params.pageSize = args.pageSize;
  if (args.leagueIds?.length) params.leagueId = args.leagueIds.join(",");
  if (args.severities?.length) params.severity = args.severities.join(",");
  if (args.issueTypes?.length) params.issueType = args.issueTypes.join(",");
  if (args.search) params.search = args.search;
  return Object.keys(params).length > 0 ? params : undefined;
}

const adjudicationQueueEndpoints = leaguesV2Api.injectEndpoints({
  endpoints: (build) => ({
    getAdjudicationQueue: build.query<
      Clubhouse_LeagueAdjudicationQueueDto,
      GetAdjudicationQueueArgs | void
    >({
      query: (args) => ({
        url: "/api/leagues/adjudication-queue",
        params: pickQueueParams(args ?? {}),
      }),
      providesTags: ["AdjudicationQueue"],
    }),
  }),
});

export const { useGetAdjudicationQueueQuery } = adjudicationQueueEndpoints;
